/**
 * Persistência local do rascunho.
 *
 * Existe porque preencher uma proposta leva tempo e envolve importar CSV: recarregar a página por
 * engano e perder tudo é o tipo de atrito que faz o vendedor voltar para a planilha.
 *
 * Escopo deliberadamente modesto: guarda só o rascunho em andamento, por usuário, no navegador. A
 * persistência que vale — versionada e auditável — é o documento no DynamoDB, a partir da Task 10.
 */

import { valoresIniciais, type ValoresFormulario } from './montarDocumento.ts';

const VERSAO = 1;
const PREFIXO = 'construtor:rascunho';

interface Envelope {
  readonly versao: number;
  readonly salvoEm: string;
  readonly valores: ValoresFormulario;
}

function chave(sub: string): string {
  return `${PREFIXO}:${sub}`;
}

/** Armazenamento pode não existir em modo privado ou estar cheio. Nunca deve quebrar o formulário. */
function armazenamento(): Storage | null {
  try {
    /*
     * O acesso pode lançar (modo privado bloqueia) ou devolver `undefined` — fora do navegador não há
     * `localStorage`. Sem esta normalização, quem chama passa pela checagem de nulo e estoura no
     * `getItem`, num ponto que não está protegido.
     *
     * A leitura passa por um tipo que declara a propriedade como opcional, e não pelo tipo global. A
     * biblioteca do DOM declara `localStorage` como sempre presente — verdade no navegador, mentira em
     * qualquer outro runtime, e é essa mentira que fazia o ESLint chamar a checagem de redundante e
     * mandar remover justamente a linha que evita a falha. Corrigir a premissa é melhor que silenciar
     * a regra: aqui o tipo passa a dizer o que o ambiente realmente garante.
     */
    const { localStorage: loja } = globalThis as { localStorage?: Storage };
    return loja ?? null;
  } catch {
    return null;
  }
}

export function salvarRascunho(sub: string, valores: ValoresFormulario): void {
  const loja = armazenamento();
  if (loja === null) return;

  const envelope: Envelope = { versao: VERSAO, salvoEm: new Date().toISOString(), valores };

  try {
    loja.setItem(chave(sub), JSON.stringify(envelope));
  } catch {
    // Cota excedida. Perder o rascunho é ruim, mas interromper o preenchimento é pior.
  }
}

export interface RascunhoRecuperado {
  readonly valores: ValoresFormulario;
  readonly salvoEm: string;
}

/** Recupera o rascunho, ou `null` quando não há, está corrompido ou é de versão antiga. */
export function recuperarRascunho(sub: string): RascunhoRecuperado | null {
  const loja = armazenamento();
  if (loja === null) return null;

  const bruto = loja.getItem(chave(sub));
  if (bruto === null) return null;

  try {
    // Tratado como `unknown` de propósito. Afirmar o tipo com `as` seria mentir: o conteúdo vem do
    // armazenamento do navegador e pode ter sido escrito por outra versão do código, ou editado à
    // mão. A validação abaixo é a única garantia real.
    const bruta: unknown = JSON.parse(bruto);

    if (typeof bruta !== 'object' || bruta === null) return null;

    const envelope = bruta as Record<string, unknown>;

    if (envelope.versao !== VERSAO) return null;
    if (typeof envelope.salvoEm !== 'string') return null;
    if (typeof envelope.valores !== 'object' || envelope.valores === null) return null;

    const gravados = envelope.valores as Partial<ValoresFormulario>;

    /*
     * O rascunho é completado com os valores iniciais, e não usado como está.
     *
     * Motivo, pago em produção: o rascunho é gravado por uma versão do código e lido por outra. Campo
     * acrescentado ao formulário depois da gravação simplesmente não existe no que voltou do
     * armazenamento, e `as ValoresFormulario` prometia um objeto completo que os dados não entregavam.
     * O formulário então renderizava com o campo em `undefined` até alguém tocá-lo, e aí quebrava a
     * tela inteira — foi o que aconteceu quando `custosAdicionais` entrou: marcar o seletor chamava
     * `.map` num valor ausente e o painel ficava em branco, sem mensagem.
     *
     * O espalhamento resolve a classe toda, não só aquele campo: chave ausente no rascunho recebe o
     * padrão, chave presente vence — inclusive quando vale `null`, que é significativo em `ptax` e
     * `itensCusto`. `JSON.stringify` descarta propriedade `undefined`, então "ausente" é exatamente a
     * forma que um campo novo tem aqui.
     *
     * Consequência deliberada: a versão do envelope não precisa subir a cada campo novo. Subir
     * descartaria o rascunho de todo vendedor com preenchimento em andamento, para resolver algo que
     * o padrão já resolve sem perda.
     */
    const trilha = gravados.trilha === 'PS' ? 'PS' : 'CS';

    return {
      valores: { ...valoresIniciais(trilha), ...gravados },
      salvoEm: envelope.salvoEm,
    };
  } catch {
    // Conteúdo inválido. Descartar em silêncio é melhor que restaurar estado inconsistente.
    return null;
  }
}

export function descartarRascunho(sub: string): void {
  armazenamento()?.removeItem(chave(sub));
}
